'use client';

import Loading from '@/app/(dynamic)/loading';
import useIsPopup from '@/hooks/use-is-popup';
import { useHighlightText, useSetLinksTargetBlank } from '@/lib/client-helpers';
import {
  fetchContentSlug,
  hasContent,
  isError,
  popupCenter,
} from '@/lib/helpers';
import {
  Content,
  CreateResponse,
  ErrorResponse,
  SeriesCategoriesResponse,
} from '@/types/sermons';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import {
  BsArrowLeftShort,
  BsBookmark,
  BsBookmarkFill,
  BsCaretLeft,
  BsCaretLeftFill,
  BsCaretRight,
  BsCaretRightFill,
  BsFileEarmarkText,
  BsPencilSquare,
  BsTrash,
} from 'react-icons/bs';
import ContentDeleteDialog from '../admin/content/content-delete-dialog';
import ContentEditDialog from '../admin/content/content-edit-dialog';
import { useNotifications } from '../providers/notification-provider';
import { useAppState } from '../providers/state-provider';
import CollapsiblePericope from '../sermons/collapsible-pericope';
import Button from './button';
import LoggedOutMessage from './logged-out-message';

export default function SermonContent({
  sermon,
  seriesCategories,
  noResults,
  mySermonsMessage,
}: {
  sermon: Content;
  seriesCategories?: SeriesCategoriesResponse;
  noResults?: boolean;
  mySermonsMessage?: string;
}) {
  const router = useRouter();

  const {
    state: {
      sermonPrepTab,
      searchParams,
      isLoading,
      user,
      token,
      mySermons,
      bookmarks,
      searchResults,
      isBrowser,
    },
    dispatch,
  } = useAppState();

  const addNotification = useNotifications();

  const isPopup = useIsPopup();

  const { isMySermons } = mySermons;
  const { ids: resultIds, resultsTerm } = searchResults;

  const pos = (resultIds || []).findIndex((id) => id === sermon.id);

  const [prevUri, setPrevUri] = useState<string | null>(null);
  const [nextUri, setNextUri] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const [content, setContent] = useState<string>(
    (!isLoading && user && hasContent(user, sermon)) || !isBrowser
      ? sermon.content
      : sermon.truncatedContent || '',
  );

  const highlightText = useHighlightText();

  const [highlightLoaded, setHighlightLoaded] = useState(false);
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (isBrowser && contentRef.current) {
      const handler = () => {
        setHighlight(false);
      };

      const elem = contentRef.current;

      elem.addEventListener('click', handler);

      return () => elem.removeEventListener('click', handler);
    }
  }, [isBrowser, contentRef.current]);

  useEffect(() => {
    let newContent = content;
      //(!isLoading && user && hasContent(user, sermon)) || !isBrowser
      //  ? sermon.content
      //  : sermon.truncatedContent || '';
    newContent = setLinksTargetBlank(newContent);
    if (resultsTerm && !highlightLoaded) {
      newContent = highlightText(newContent, resultsTerm);
      setHighlightLoaded(true);
      setHighlight(true);
    } else if (resultsTerm && highlightLoaded && highlight) {
      newContent = highlightText(newContent, resultsTerm);
    }
    setContent(newContent);
  }, [
    isLoading,
    user,
    sermon,
    isBrowser,
    resultsTerm,
    highlightLoaded,
    highlight,
  ]);

  useEffect(() => {
    if (resultsTerm !== null && !resultIds.includes(sermon.id)) {
      dispatch({
        type: 'searchResults',
        payload: {
          ...searchResults,
          resultsTerm: null,
        },
      });
    }
  }, [searchResults, resultIds]);

  useEffect(() => {
    if (!isMySermons && sermon.userId) {
      dispatch({
        type: 'mySermons',
        payload: { ...mySermons, isMySermons: true },
      });

      if (user?.id !== +sermon.userId && !user?.isAdmin) {
        router.replace('/');
      }
    }
  }, [sermon, user, isMySermons]);

  const pericopeCode = searchParams.get('pericopeCode');

  const pericope = sermon.pericopes?.filter(
    (p) => !pericopeCode || p.number === pericopeCode,
  )?.[0];

  const seriesCategoryParam = searchParams.get('seriesCategory');
  const seriesCategoryId =
    seriesCategoryParam === null
      ? null
      : isNaN(+seriesCategoryParam)
        ? null
        : +seriesCategoryParam;
  const seriesParam = searchParams.get('series');
  const seriesId =
    seriesParam === null ? null : isNaN(+seriesParam) ? null : +seriesParam;

  const seriesCategory = seriesCategories?.seriesCategories.find(
    (c) => c.id === seriesCategoryId,
  );
  const series = seriesCategory?.series.find((s) => s.id === seriesId);

  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  const setLinksTargetBlank = useSetLinksTargetBlank();

  const pathname = usePathname();

  const seriesIndex =
    series === undefined
      ? -1
      : series.sermons.findIndex((s) => s.id === sermon.id);
  const seriesTotal = series?.sermons.length || 0;
  const prevSermon = series?.sermons[seriesIndex - 1];
  const prevSermonUrl = prevSermon && new URL('https://null');
  let pathParts = pathname.split('/');
  if (pathParts && prevSermon?.id) {
    pathParts.pop();
    pathParts.pop();
    pathParts.push(prevSermon.slug || '_', prevSermon.id);
  }
  if (prevSermonUrl && pathParts) {
    prevSermonUrl.pathname = pathParts.join('/');
    prevSermonUrl.search = `?${searchParams}`;
  }
  const nextSermon = series?.sermons[seriesIndex + 1];
  const nextSermonUrl = nextSermon && new URL('https://null');
  pathParts = pathname.split('/');
  if (pathParts && nextSermon?.id) {
    pathParts.pop();
    pathParts.pop();
    pathParts.push(nextSermon.slug || '_', nextSermon.id);
  }
  if (nextSermonUrl && pathParts) {
    nextSermonUrl.pathname = pathParts.join('/');
    nextSermonUrl.search = `?${searchParams}`;
  }

  useEffect(() => {
    (async () => {
      if (pos > -1) {
        const prevId = pos > 0 ? resultIds?.[pos - 1] : null;
        const nextId =
          pos < (resultIds?.length || 0) ? resultIds?.[pos + 1] : null;

        if (prevId) {
          const result = await fetchContentSlug(`${prevId}`);
          if (!isError(result)) {
            const url = new URL('https://null');
            url.pathname = `sermon${isPopup ? '/popout' : ''}/${
              result.slug
            }/${prevId}`;
            url.search = searchParams.toString();
            setPrevUri(`${url.pathname}${url.search}`);
          }
        }
        if (nextId) {
          const result = await fetchContentSlug(`${nextId}`);
          if (!isError(result)) {
            const url = new URL('https://null');
            url.pathname = `sermon${isPopup ? '/popout' : ''}/${
              result.slug
            }/${nextId}`;
            url.search = searchParams.toString();
            setNextUri(`${url.pathname}${url.search}`);
          }
        }
      }
    })();
  }, [pos, resultIds]);

  const [editingContent, setEditingContent] = useState(false);
  const [deletingContent, setDeletingContent] = useState(false);

  return (
    <>
      <section
        className={classNames({
          hidden: sermonPrepTab !== 'content',
        })}
      >
        {sermon.contentTitle && <CollapsiblePericope pericope={pericope} />}
        <section>
          {sermon.contentTitle && (
            <header className='grid grid-cols-2 border border-t-0 border-neutral-300 bg-neutral-100 p-4'>
              {seriesCategory && series && (
                <div className='col-span-2 grid grid-cols-[1fr_max-content_1fr]'>
                  <div className='flex items-center justify-end'>
                    <Link href={`/sermon-series?category=${seriesCategory.id}`}>
                      <BsArrowLeftShort className='inline-block text-2xl text-black' />
                    </Link>
                  </div>
                  <div className='flex items-center'>
                    <div>Sermon Series: {series.title}</div>
                  </div>
                  <div></div>
                </div>
              )}
              <div>
                <div>
                  <span className='font-bold'>{sermon.contentTitle}</span>

                  {user && !isLoading && !isMySermons && (
                    <>
                      {' '}
                      {bookmarks && bookmarks.includes(sermon.id) && (
                        <button
                          type='button'
                          className='action'
                          onClick={async () => {
                            if (bookmarkLoading) {
                              return;
                            }

                            setBookmarkLoading(true);

                            const res = await fetch(
                              `${process.env.NEXT_PUBLIC_API_BASE_URL}/contents/bookmarked-sermons/${sermon.id}`,
                              {
                                method: 'DELETE',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                cache: 'no-store',
                                next: {
                                  tags: ['all', 'contents', 'bookmarks'],
                                },
                              },
                            );

                            const contentsResponse:
                              | CreateResponse
                              | ErrorResponse = await res.json();
                            if (isError(contentsResponse)) {
                              addNotification({
                                type: 'error',
                                message: contentsResponse.message,
                              });
                            } else {
                              dispatch({
                                type: 'bookmarks',
                                payload: bookmarks.filter(
                                  (b) => b !== sermon.id,
                                ),
                              });
                              addNotification({
                                type: 'success',
                                message: contentsResponse.success,
                              });
                            }

                            setBookmarkLoading(false);
                          }}
                        >
                          {bookmarkLoading && (
                            <Image
                              className='inline-block'
                              src={'/img/ajax-loader.gif'}
                              width={16}
                              height={11}
                              alt='Loading...'
                              unoptimized
                            />
                          )}
                          {!bookmarkLoading && (
                            <BsBookmarkFill className='inline-block' />
                          )}
                        </button>
                      )}
                      {!bookmarks ||
                        (!bookmarks.includes(sermon.id) && (
                          <button
                            type='button'
                            className='action'
                            onClick={async () => {
                              if (bookmarkLoading) {
                                return;
                              }

                              setBookmarkLoading(true);

                              const res = await fetch(
                                `${process.env.NEXT_PUBLIC_API_BASE_URL}/contents/bookmarks`,
                                {
                                  method: 'POST',
                                  body: JSON.stringify({
                                    sermonId: sermon.id,
                                  }),
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${token}`,
                                  },
                                  cache: 'no-store',
                                  next: {
                                    tags: ['all', 'contents', 'bookmarks'],
                                  },
                                },
                              );

                              const contentsResponse:
                                | CreateResponse
                                | ErrorResponse = await res.json();
                              if (isError(contentsResponse)) {
                                addNotification({
                                  type: 'error',
                                  message: contentsResponse.message,
                                });
                              } else {
                                dispatch({
                                  type: 'bookmarks',
                                  payload: [...bookmarks, sermon.id],
                                });
                                addNotification({
                                  type: 'success',
                                  message:
                                    'The bookmark was added successfully.',
                                });
                              }

                              setBookmarkLoading(false);
                            }}
                          >
                            {bookmarkLoading && (
                              <Image
                                className='inline-block'
                                src={'/img/ajax-loader.gif'}
                                width={16}
                                height={11}
                                alt='Loading...'
                                unoptimized
                              />
                            )}
                            {!bookmarkLoading && (
                              <BsBookmark className='inline-block' />
                            )}
                          </button>
                        ))}
                    </>
                  )}
                </div>
                <span>
                  {sermon.displayedScripture?.[0]?.displayedScripture ||
                    sermon.pericopes?.map((p) => p.fullReference).join(', ')}
                </span>
              </div>
              <div className='text-right'>
                <span>{sermon.tbTocType && sermon.tbTocType.tocName}</span>
                {series && (
                  <span>
                    <span> </span>
                    {prevSermon && prevSermonUrl && (
                      <Link
                        href={`${prevSermonUrl.pathname}${prevSermonUrl.search}`}
                      >
                        <BsCaretLeftFill className='inline-block align-text-bottom text-sermons-dark' />
                      </Link>
                    )}
                    {!(prevSermon && prevSermonUrl) && (
                      <BsCaretLeft className='inline-block align-text-bottom text-sermons-dark' />
                    )}
                    <span>
                      {' '}
                      {seriesIndex + 1} of {seriesTotal}{' '}
                    </span>
                    {nextSermon && nextSermonUrl && (
                      <Link
                        href={`${nextSermonUrl.pathname}${nextSermonUrl.search}`}
                      >
                        <BsCaretRightFill className='inline-block align-text-bottom text-sermons-dark' />
                      </Link>
                    )}
                    {!(nextSermon && nextSermonUrl) && (
                      <BsCaretRight className='inline-block align-text-bottom text-sermons-dark' />
                    )}
                  </span>
                )}
                {pos > -1 && (
                  <span>
                    <span> </span>
                    {prevUri && (
                      <Link href={prevUri} scroll={!isPopup}>
                        <BsCaretLeftFill className='inline-block align-text-bottom text-sermons-dark' />
                      </Link>
                    )}
                    {!prevUri && (
                      <BsCaretLeft className='inline-block align-text-bottom text-sermons-dark' />
                    )}
                    <span>
                      {' '}
                      {pos + 1} of {resultIds?.length}{' '}
                    </span>
                    {nextUri && (
                      <Link href={nextUri} scroll={!isPopup}>
                        <BsCaretRightFill className='inline-block align-text-bottom text-sermons-dark' />
                      </Link>
                    )}
                    {!nextUri && (
                      <BsCaretRight className='inline-block align-text-bottom text-sermons-dark' />
                    )}
                  </span>
                )}
                <br />
                {(sermon.authors?.length || 0) > 0 && (
                  <span className='relative inline-block'>
                    <span>by </span>
                    <span
                      className='relative inline-block'
                      aria-label={sermon.authors
                        ?.map((a) => `${a.firstname} ${a.lastname}`.trim())
                        .join(', ')}
                    >
                      <span className='inline-block max-w-80 truncate align-top'>
                        {sermon.authors?.map((author, index) => (
                          <span key={author.id}>
                            {index > 0 && <span>, </span>}
                            {author.firstname} {author.lastname}
                          </span>
                        ))}
                      </span>
                    </span>
                  </span>
                )}
              </div>
            </header>
          )}
          <article className='relative'>
            {(!isBrowser || isLoading) && (
              <Loading className='absolute inset-0 z-10 bg-white' />
            )}
            <div className='border border-t-0 border-neutral-300 p-4'>
              {sermon.contentTitle && (
                <div className='float-right p-4 pr-0 pt-0'>
                  <Link
                    href={`/sermon${isPopup ? '/popout' : ''}/${
                      sermon.slug || '_'
                    }/${sermon.id}/print`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='mx-2 inline-flex size-10 items-center justify-center rounded text-3xl'
                    onClick={(e) => {
                      e.preventDefault();
                      popupCenter({
                        url: `/sermon/${sermon.slug || '_'}/${sermon.id}/print`,
                        title: '_blank',
                        w: 600,
                        h: 800,
                      });
                    }}
                    scroll={!isPopup}
                  >
                    <Button className='inline-flex size-10 items-center justify-center text-3xl'>
                      <BsFileEarmarkText />
                    </Button>
                  </Link>
                  <Link
                    href={`/sermon${isPopup ? '/popout' : ''}/${
                      sermon.slug || '_'
                    }/${sermon.id}/print?print=true`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='mr-2 inline-flex size-10 items-center justify-center rounded align-top text-3xl'
                    onClick={(e) => {
                      e.preventDefault();
                      popupCenter({
                        url: `/sermon/${sermon.slug || '_'}/${
                          sermon.id
                        }/print?print=true`,
                        title: '_blank',
                        w: 600,
                        h: 800,
                      });
                    }}
                    scroll={!isPopup}
                  >
                    <Button className='inline-flex size-10 items-center justify-center text-3xl'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        enableBackground='new 0 0 512 512'
                        height={27}
                        id='Layer_1'
                        version='1.1'
                        viewBox='0 0 512 512'
                        width={27}
                        stroke='#FFF'
                        strokeWidth={12}
                      >
                        <g>
                          <path
                            d='M484.281,210.609l-28.906-33.031C445.594,166.406,431.469,160,416.625,160H384V56c0-22.094-17.906-40-40-40   H237.656c-8.75,0-17.141,3.469-23.313,9.656l-76.688,76.688c-6.188,6.188-9.656,14.563-9.656,23.313V160h-32.64   c-14.844,0-28.969,6.406-38.734,17.578l-28.922,33.031C20.156,219.25,16,230.313,16,241.781V352c0,35.344,28.656,64,64,64h27.344   l-9.875,29.625c-2.656,7.969-1.313,16.719,3.594,23.531C105.97,475.968,113.844,480,122.25,480h267.594   c8.375,0,16.25-4.016,21.156-10.813c4.922-6.798,6.281-15.531,3.656-23.484L404.844,416H432c35.344,0,64-28.656,64-64V241.781   C496,230.313,491.828,219.25,484.281,210.609z M224,38.625V88c0,13.234-10.781,24-24,24h-49.375L224,38.625z M144,128h56   c22.094,0,40-17.906,40-40V32h104c13.219,0,24,10.766,24,24v136H144V128z M39.75,221.141l28.906-33.031   C75.391,180.422,85.125,176,95.359,176H128v16h-8c-4.438,0-8,3.578-8,8s3.563,8,8,8h272c4.406,0,8-3.578,8-8s-3.594-8-8-8h-8v-16   h32.625c10.234,0,19.969,4.422,26.703,12.109l28.922,33.031c0.781,0.891,1.406,1.891,2.063,2.859H37.672   C38.344,223.031,38.969,222.031,39.75,221.141z M96,400v-32c0-17.656,14.344-32,32-32h6l-21.344,64H96z M398.063,459.813   c-1.875,2.625-4.938,4.188-8.188,4.188h-267.75c-3.25,0-6.313-1.563-8.219-4.188c-1.875-2.625-2.406-6.03-1.406-9.094L150.5,336   H362l37.5,114.719C400.5,453.781,399.969,457.188,398.063,459.813z M416,400h-16.422l-21.109-64H384c17.656,0,32,14.344,32,32V400z    M480,352c0,26.469-21.531,48-48,48v-32c0-26.516-21.5-48-48-48H128c-26.516,0-48,21.484-48,48v32c-26.469,0-48-21.531-48-48   V241.781c0-0.594,0.125-1.188,0.156-1.781h447.672c0.047,0.594,0.172,1.188,0.172,1.781V352z M352,424c0,4.422-3.594,8-8,8H168   c-4.438,0-8-3.578-8-8s3.563-8,8-8h176C348.406,416,352,419.578,352,424z M167.984,392c0-4.422,3.578-8,8-8h161.609   c4.438,0,8,3.578,8,8s-3.563,8-8,8H175.984C171.563,400,167.984,396.422,167.984,392z M448,264c0,4.422-3.594,8-8,8h-32   c-4.438,0-8-3.578-8-8s3.563-8,8-8h32C444.406,256,448,259.578,448,264z M448,296c0,4.422-3.594,8-8,8h-32c-4.438,0-8-3.578-8-8   s3.563-8,8-8h32C444.406,288,448,291.578,448,296z M176,360c0-4.422,3.563-8,8-8h144c4.406,0,8,3.578,8,8s-3.594,8-8,8H184   C179.563,368,176,364.422,176,360z'
                            fill='#FFF'
                          />
                        </g>
                      </svg>
                    </Button>
                  </Link>
                  {user && user.isAdmin && (
                    <>
                      <Button
                        className='mr-2 inline-flex size-10 items-center justify-center text-3xl'
                        onClick={(e) => {
                          e.preventDefault();
                          setEditingContent(true);
                        }}
                      >
                        <BsPencilSquare />
                      </Button>
                      <Button
                        className='inline-flex size-10 items-center justify-center text-3xl'
                        onClick={(e) => {
                          e.preventDefault();
                          setDeletingContent(true);
                        }}
                      >
                        <BsTrash />
                      </Button>
                    </>
                  )}
                </div>
              )}
              {!noResults && (
                <div
                  ref={contentRef}
                  className='sermon-content'
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                ></div>
              )}
              {noResults && isMySermons && (
                <div
                  className='sermon-content'
                  dangerouslySetInnerHTML={{
                    __html: setLinksTargetBlank(mySermonsMessage || ''),
                  }}
                ></div>
              )}
              {noResults && !isMySermons && (
                <div className='sermon-content'>
                  Please click on &quot;Tools&quot; above for sermon prep
                  resources as there are no sermons for this scriptural passage.
                </div>
              )}
              {!isLoading &&
                (!user || !hasContent(user, sermon)) &&
                isBrowser && <LoggedOutMessage />}
              {(sermon.publisher ||
                sermon.publicationTitle ||
                (sermon.authors?.length || 0) > 0) && (
                <div className='mt-4 flex items-center justify-end text-xs'>
                  <span>
                    {sermon.publisher && <span>{sermon.publisher}, </span>}
                    {sermon.publicationTitle && (
                      <span>
                        <span className='underline'>
                          {sermon.publicationTitle}
                        </span>
                        {(sermon.authors?.length || 0) > 0 && <span>, </span>}
                      </span>
                    )}
                    {(sermon.authors?.length || 0) > 0 && (
                      <span>
                        by{' '}
                        {sermon.authors?.map((author, index) => (
                          <span key={author.id}>
                            {index > 0 && <span>, </span>}
                            {author.firstname} {author.lastname}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </article>
        </section>
      </section>
      <ContentEditDialog
        sermon={sermon}
        isOpen={editingContent}
        setOpen={setEditingContent}
        setError={(message) => addNotification({ type: 'error', message })}
        setSuccess={(message) => addNotification({ type: 'success', message })}
        onSave={() => router.refresh()}
      />
      <ContentDeleteDialog
        sermon={sermon}
        isOpen={deletingContent}
        setOpen={setDeletingContent}
        onDelete={() => router.refresh()}
      />
    </>
  );
}
